<template>
  <div ref="listHeader">
    <div class="d-flex flex-row justify-space-between">
      <div class="d-flex ga-2">
        <food-search v-bind="{ id, code }" />
        <add-food-dialog v-if="!readonly" v-bind="{ id, code }" />
      </div>
      <v-menu :close-on-content-click="false" location="bottom left">
        <template #activator="{ props }">
          <v-btn color="primary" icon="$options" size="small" v-bind="props" />
        </template>
        <v-list>
          <v-list-item>
            <template #prepend>
              <v-list-item-action>
                <v-switch v-model="showGlobalName" />
              </v-list-item-action>
            </template>
            <v-list-item-title>{{ $t('fdbs.showGlobalName') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <v-divider class="mt-2" />
  </div>
  <v-treeview
    v-model:activated="activated"
    v-model:opened="opened"
    activatable
    color="primary"
    density="compact"
    item-title="name"
    item-value="key"
    :items="items"
    :load-children="fetchCategoryContent"
    :style="listStyle"
    transition
  >
    <template #prepend="{ item }">
      <v-icon v-if="item.type === 'foods'" icon="$foods" start />
    </template>
    <template #title="{ item }">
      <a :title="item[itemText] || ''" @click="openItem(item)">{{ item[itemText] }}</a>
    </template>
  </v-treeview>
</template>

<script lang="ts" setup>
import type {
  CategoryContentsResponse,
  CategoryListEntry,
  FoodListEntry,
} from '@intake24/common/types/http/admin';

import { useElementBounding } from '@vueuse/core';
import { computed, onMounted, onUpdated, ref, shallowRef, useTemplateRef } from 'vue';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';

import { useHttp } from '@intake24/admin/services';
import { randomString } from '@intake24/common/util';
import { useI18n } from '@intake24/ui';

import AddFoodDialog from './add-food-dialog.vue';
import FoodSearch from './food-search.vue';

export type TreeItem = ((CategoryListEntry & { children: TreeItem[] }) | FoodListEntry) & {
  key: string;
  type: string;
};

defineOptions({ name: 'FoodExplorer' });

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  code: {
    type: String,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();
const http = useHttp();
const { i18n } = useI18n();

const activated = ref<string[]>([]);
const opened = ref<string[]>([]);
const items = ref<TreeItem[]>([]);
const showGlobalName = ref<boolean>(false);
const activatedEntryId = ref<string | null>(null);
const activatedEntryCategories = ref<string[]>([]);

const itemText = computed(() => (showGlobalName.value ? 'englishName' : 'name'));

async function fetchRootCategories() {
  const { data } = await http.get<CategoryListEntry[]>(
    `admin/fdbs/${props.id}/categories/root`,
  );

  const noCategory: TreeItem = {
    key: randomString(8),
    type: 'categories',
    id: 'no-category',
    code: 'no-category',
    localeId: props.id,
    name: i18n.t('fdbs.categories.noCategory'),
    englishName: i18n.t('fdbs.categories.noCategory'),
    hidden: false,
    children: [],
  };

  items.value = [
    noCategory,
    ...data.map(category => ({
      ...category,
      key: randomString(8),
      type: 'categories',
      children: [],
    })),
  ];
}

async function fetchCategoryContent(category: TreeItem) {
  const {
    data: { categories, foods },
  } = await http.get<CategoryContentsResponse>(
    `admin/fdbs/${props.id}/categories/${category.id}/contents`,
  );

  if (!('children' in category))
    return;

  category.children.push(
    ...categories.map(item => ({
      ...item,
      key: randomString(8),
      type: 'categories',
      children: [],
    })),
    ...foods.map(item => ({
      ...item,
      key: randomString(8),
      type: 'foods',
    })),
  );
}

async function openItem(item: TreeItem) {
  if (activatedEntryId.value === item.id)
    return;

  activatedEntryId.value = item.id;
  activatedEntryCategories.value = [];

  await router.push({
    name: `fdbs-${item.type}`,
    params: { id: props.id, entryId: item.id },
  });
}

async function findActiveInChildren(children: TreeItem[]) {
  if (!children.length)
    return;

  for (const child of children) {
    if (activatedEntryId.value === child.id) {
      activated.value = [child.key];
      return true;
    }

    if (activatedEntryCategories.value.includes(child.id)) {
      await fetchCategoryContent(child);
      opened.value.push(child.key);

      if (!('children' in child) || !child.children.length)
        continue;

      const found = await findActiveInChildren(child.children);
      if (found)
        return true;
    }
  }
}

async function findActiveInTree(entryId: string, type: string) {
  if (!['categories', 'foods'].includes(type))
    return;

  const { data } = await http.get<string[]>(`admin/fdbs/${props.id}/${type}/${entryId}/categories`);

  activatedEntryId.value = entryId;
  activatedEntryCategories.value = data.reverse();

  await findActiveInChildren(items.value);
}

const listViewOffset = shallowRef();
const listHeader = useTemplateRef('listHeader');
const listHeaderHeight = useElementBounding(listHeader).height;
const listStyle = computed(() => {
  return {
    minHeight: `calc(192px - ${listHeaderHeight.value}px)`,
    height: `calc(100vh - ${listViewOffset.value}px - ${listHeaderHeight.value}px)`,
    overflowY: 'auto',
  };
});

const activeUpdated = shallowRef(false);
onBeforeRouteUpdate((to) => {
  if (to.params.entryId !== activatedEntryId.value) {
    findActiveInTree(to.params.entryId?.toString(), to.meta.module.current);
    activeUpdated.value = true;
  }
});

onMounted(async () => {
  await fetchRootCategories();
  await findActiveInTree(route.params.entryId?.toString(), route.meta.module.current);
});

onUpdated(() => {
  // retrieve height of following elements to offset
  listViewOffset.value = (document.getElementById('header')?.offsetHeight || 0)
    + (document.getElementById('footer')?.offsetHeight || 0)
    + (document.getElementById('entryBreadcrumb')?.offsetHeight || 0)
    + (document.getElementById('entryTabs')?.offsetHeight || 0);

  if (activeUpdated.value) {
    // scroll to active item in treeview
    document.querySelector('.v-list-item--active')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    activeUpdated.value = false;
  }
});
</script>

<style lang="scss">
.v-list-item__prepend .v-treeview-item__level {
  max-width: fit-content;
}
</style>
